import type { NewsListBlockProps, NewsListResponse } from "./types";

import {
  getAvailableYears,
  getAvailableMonths,
  formatMonth,
} from "@/utils/date";
import Link from "@/components/link";
import { NewsArticleCard } from "@/components/blocks/news-article-card";
import { useState, useEffect, useRef } from "react";
import {
  Field,
  Heading,
  Pagination,
  Select,
  usePagination,
} from "@digdir/designsystemet-react";
import { ArrowRightIcon } from "@navikt/aksel-icons";

export type { NewsListBlockProps };

export const NewsListBlock = ({
  blockId,
  blockHeading,
  newsPages,
  totalCount,
  availableDates,
  initialYear,
  pageSize,
  disablePaging,
  disableFiltering,
  showArchiveButton,
  archiveUrl,
}: NewsListBlockProps) => {
  // Track screensize to adjust number of pages shown in pagination
  const [isSmallScreen, setIsSmallScreen] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;

    const update = () => setIsSmallScreen(window.innerWidth < 768);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  // Filtering (options come from the server, not from the loaded articles)
  const years = getAvailableYears(availableDates);
  const [selectedYear, setSelectedYear] = useState<number | "">(
    initialYear ?? ""
  );
  const months =
    selectedYear !== "" ? getAvailableMonths(availableDates, selectedYear) : [];
  const [selectedMonth, setSelectedMonth] = useState<number | "">("");

  // Newsarticles shown at the current page, fetched from the backend
  const [articles, setArticles] = useState(newsPages);
  const [total, setTotal] = useState(totalCount);
  const [isLoading, setIsLoading] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = disablePaging
    ? 1
    : Math.max(1, Math.ceil(total / pageSize));
  const pagesShown = Math.min(isSmallScreen ? 3 : 7, totalPages);

  const { pages, prevButtonProps, nextButtonProps, hasNext, hasPrev } =
    usePagination({
      currentPage,
      setCurrentPage,
      totalPages: totalPages,
      showPages: pagesShown,
    });

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage, setCurrentPage]);

  // Fetch whenever page or filter changes. The key guard skips the request for
  // the server-rendered first page
  const lastFetchedKey = useRef(`1|${initialYear ?? ""}|`);
  useEffect(() => {
    const key = `${currentPage}|${selectedYear}|${selectedMonth}`;
    if (key === lastFetchedKey.current) return;
    lastFetchedKey.current = key;

    const controller = new AbortController();
    const params = new URLSearchParams({ blockId: String(blockId) });
    params.set("page", String(currentPage));
    if (selectedYear) params.set("year", String(selectedYear));
    if (selectedMonth) params.set("month", String(selectedMonth));

    setIsLoading(true);
    fetch(`/api/NewsListApi/getnews?${params}`, {
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch news");
        return res.json() as Promise<NewsListResponse>;
      })
      .then((data) => {
        setArticles(data.items);
        setTotal(data.totalCount);
        sectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      })
      .catch((err) => {
        if (err.name !== "AbortError") console.error("Error fetching news", err);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    // A newer click cancels the in-flight request, so responses can't arrive
    // out of order. Clearing the key lets a re-run (e.g. StrictMode) fetch again
    return () => {
      controller.abort();
      lastFetchedKey.current = "";
    };
  }, [blockId, currentPage, selectedYear, selectedMonth]);

  return (
    <section ref={sectionRef} className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 justify-between md:flex-row">
        {blockHeading && (
          <Heading className="heading-md mb-0">{blockHeading}</Heading>
        )}

        {/* Filtering */}
        {!disableFiltering && (
          <div className="flex flex-col gap-2 md:flex-row md:ml-auto">
            <Field>
              <Select
                value={selectedYear}
                aria-label="Velg år"
                onChange={(e) => {
                  setSelectedYear(Number(e.target.value) || "");
                  setSelectedMonth("");
                  setCurrentPage(1);
                }}
                className="h-10.5 py-1 border border-neutralNsf-subtle md:h-12 xl:h-13"
              >
                <Select.Option value="" className="text-md">
                  Alle år
                </Select.Option>
                {years.map((year) => (
                  <Select.Option key={year} value={year} className="text-md">
                    {year}
                  </Select.Option>
                ))}
              </Select>
            </Field>

            <Field>
              <Select
                value={selectedMonth}
                aria-label="Velg måned"
                onChange={(e) => {
                  setSelectedMonth(Number(e.target.value) || "");
                  setCurrentPage(1);
                }}
                disabled={!selectedYear}
                className="h-10.5 py-1 border border-neutralNsf-subtle md:h-12 xl:h-13"
              >
                <Select.Option value="" className="text-md">
                  Alle måneder
                </Select.Option>
                {months.map((m) => (
                  <Select.Option key={m} value={m} className="text-md">
                    {formatMonth(m)}
                  </Select.Option>
                ))}
              </Select>
            </Field>
          </div>
        )}
      </div>

      {/* News article list */}
      <div
        className="grid justify-start gap-5 lg:grid-cols-3 transition-opacity"
        aria-busy={isLoading}
        style={{ opacity: isLoading ? 0.5 : 1 }}
      >
        {articles.map((article) => (
          <NewsArticleCard
            key={`${article.heading}-${article.published}`}
            url={article.url}
            heading={article.heading}
            introText={article.mainIntro}
            imageUrl={article.imageUrl ? `${article.imageUrl}?width=416` : ''}
            imgAltText={article.imgAltText}
          />
        ))}
      </div>

      {/* Pagination */}
      {!disablePaging && (
        <Pagination className="pagination">
          <Pagination.List className="justify-center">
            <Pagination.Item>
              <Pagination.Button
                aria-label="Forrige side"
                disabled={!hasPrev || isLoading}
                className="px-2 md:px-4"
                {...prevButtonProps}
              >
                <span className="hidden md:inline">Forrige</span>
              </Pagination.Button>
            </Pagination.Item>

            {pages.map(({ page, itemKey, buttonProps }) => {
              if (page === "ellipsis") {
                return <Pagination.Item key={itemKey} />;
              }
              if (typeof page === "number" && page > 0 && page <= totalPages) {
                return (
                  <Pagination.Item key={itemKey}>
                    <Pagination.Button
                      asChild
                      aria-label={`Side ${page}`}
                      {...buttonProps}
                    >
                      <span>{page}</span>
                    </Pagination.Button>
                  </Pagination.Item>
                );
              }
              return null;
            })}

            <Pagination.Item>
              <Pagination.Button
                aria-label="Neste side"
                disabled={!hasNext || isLoading}
                className="px-2 md:px-4"
                {...nextButtonProps}
              >
                <span className="hidden md:inline">Neste</span>
              </Pagination.Button>
            </Pagination.Item>
          </Pagination.List>
        </Pagination>
      )}

      {/* Link to newsarchive page */}
      {showArchiveButton && archiveUrl && (
        <Link href={archiveUrl} className="flex justify-end w-fit ml-auto">
          Se flere nyheter
          <ArrowRightIcon />
        </Link>
      )}
    </section>
  );
};
